import { milestoneTier, TIERS, titleCase } from '../data';
import { RunSummary } from '../engine';

// Square share card that mirrors the Figma "Final Share Card Templates"
// (Marketing Mini Tools, node 14001-7783): navy cabinet, a semicircular energy
// gauge that fills by tier, the run's kWh, the earned tier + its badge icon, a
// five-stop tier progress bar, and the challenge footer. Self-contained (no color
// mode, no external fonts) so the shared PNG looks identical everywhere.
const NAVY = '#0a1220';
const YELLOW = '#fec351';
const PAPER = '#ffffff';
const MUTED = '#7e8289';

// Authoring space matches the 600x600 Figma frame; the canvas is rendered at 2x for retina.
const SIZE = 600;
const SCALE = 2;
const CX = SIZE / 2;
const MARGIN = 44;
const FONT = '-apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

// Semicircular gauge: center x = CX, baseline y, radius — matching the template arc (r=150).
const GAUGE = { r: 150, yb: 268 };

// How far the yellow arc sweeps for each tier (1..5), derived from the template fill vectors
// (bounding widths 4, 35.5, 150, 256, 300 over a 300px-wide arc → sweep fractions of 180°).
const TIER_FILL = [0.074, 0.22, 0.5, 0.75, 1] as const;

type IconSpec = {
  cap?: CanvasLineCap;
  d: string[];
  mode: 'fill' | 'stroke';
  rule?: CanvasFillRule;
  sw?: number;
  vb: [number, number];
};

// Tier badge icons, lifted verbatim from the Figma templates (stroke/fill + viewBox preserved so
// they scale crisply). Order matches TIERS: Rookie, Amp Cadet, Watt Scout, Grid Hero, Solar Legend.
const TIER_ICONS: IconSpec[] = [
  {
    d: [
      'M12.4875 4.05V0H14.5125V4.05H12.4875Z',
      'M13.5 20.25C17.2279 20.25 20.25 17.2279 20.25 13.5C20.25 9.77208 17.2279 6.75 13.5 6.75C9.77208 6.75 6.75 9.77208 6.75 13.5C6.75 17.2279 9.77208 20.25 13.5 20.25ZM13.5 18.225C16.1095 18.225 18.225 16.1095 18.225 13.5C18.225 10.8905 16.1095 8.775 13.5 8.775C10.8905 8.775 8.775 10.8905 8.775 13.5C8.775 16.1095 10.8905 18.225 13.5 18.225Z',
      'M22.33 3.2381L19.4663 6.10189L20.8982 7.53378L23.7619 4.66999L22.33 3.2381Z',
      'M22.95 12.4875H27V14.5125H22.95V12.4875Z',
      'M22.33 23.7619L23.7619 22.33L20.8981 19.4662L19.4662 20.8981L22.33 23.7619Z',
      'M12.4875 27V22.95H14.5125V27H12.4875Z',
      'M6.10187 19.4662L3.23808 22.33L4.66998 23.7619L7.53376 20.8981L6.10187 19.4662Z',
      'M0 12.4875H4.05V14.5125H0V12.4875Z',
      'M7.5338 6.10187L4.67002 3.23809L3.23813 4.66998L6.10191 7.53376L7.5338 6.10187Z',
    ],
    mode: 'fill',
    rule: 'evenodd',
    vb: [27, 27],
  },
  {
    d: [
      'M14.85 0L0 16.2H8.1L6.75 27L21.6 9.45H13.5L14.85 0ZM17.2339 11.475H11.1652L11.9197 6.19353L4.6033 14.175H10.3939L9.60428 20.4918L17.2339 11.475Z',
    ],
    mode: 'fill',
    rule: 'evenodd',
    vb: [21.6, 27],
  },
  {
    cap: 'round',
    d: [
      'M17 29.5C24.4558 29.5 30.5 23.4558 30.5 16C30.5 8.54416 24.4558 2.5 17 2.5C9.54416 2.5 3.5 8.54416 3.5 16C3.5 23.4558 9.54416 29.5 17 29.5Z',
      'M22.5 10.5L18.9744 17.9744L11.5 21.5L15.0256 14.0256L22.5 10.5Z',
    ],
    mode: 'stroke',
    sw: 1.95,
    vb: [32, 32],
  },
  {
    cap: 'round',
    d: [
      'M15.5 32.875L27 28.5625V20.6562C27 13.4688 21.825 8.725 15.5 6.28125C9.175 8.725 4 13.4688 4 20.6562V28.5625L15.5 32.875Z',
    ],
    mode: 'stroke',
    sw: 2.5875,
    vb: [32, 38],
  },
  {
    cap: 'butt',
    d: [
      'M11.002 15.7303L8.71777 2.78611L15.5706 7.05008',
      'M21.0539 15.7303L23.3382 2.78611L16.4854 7.05008',
      'M16.0279 15.7301C20.7378 15.7301 24.5559 19.5482 24.5559 24.258C24.5559 28.9679 20.7378 32.786 16.0279 32.786C11.3181 32.786 7.5 28.9679 7.5 24.258C7.5 19.5482 11.3181 15.7301 16.0279 15.7301Z',
      'M16.3125 24.6527L17.1221 24.6146L16.4941 24.1107L16.71 23.3177L16.0273 23.764L15.3447 23.3177L15.5615 24.1107L14.9326 24.6146L15.7432 24.6527L16.0273 25.4056L16.3125 24.6527Z',
    ],
    mode: 'stroke',
    sw: 2.74112,
    vb: [32, 40.286],
  },
];

type ShareInput = {
  /** outerHTML of the rendered DS.IconLogo — rasterized into the header so the wordmark stays current */
  logoSvg?: string;
  summary: RunSummary;
};

/** Rasterize the live wordmark SVG, tinted white for the navy card; resolves null if anything goes wrong.
 *  An inline SVG's outerHTML lacks xmlns, dimensions, and its styled-components fill — all three are
 *  required for the blob to load as an image, so they're injected here. */
const loadLogo = (logoSvg?: string): Promise<HTMLImageElement | null> => {
  if (!logoSvg) return Promise.resolve(null);
  const viewBox = /viewBox="0 0 ([\d.]+) ([\d.]+)"/.exec(logoSvg);
  const ratio = viewBox ? Number(viewBox[1]) / Number(viewBox[2]) : 4;
  const rasterHeight = 88;
  const namespaced = logoSvg.includes('xmlns=')
    ? logoSvg
    : logoSvg.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
  const tinted = namespaced
    .replace(/ fill="(?!none)[^"]*"/g, '')
    .replace('<svg', `<svg fill="${PAPER}" height="${rasterHeight}" width="${Math.round(rasterHeight * ratio)}"`);
  const url = URL.createObjectURL(new Blob([tinted], { type: 'image/svg+xml' }));
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    image.src = url;
  });
};

/** Draw a badge icon centered at (cx, cy), scaled to a target height, in its own transform. */
const drawIcon = (ctx: CanvasRenderingContext2D, icon: IconSpec, cx: number, cy: number, height: number): void => {
  const s = height / icon.vb[1];
  const width = icon.vb[0] * s;
  ctx.save();
  ctx.translate(cx - width / 2, cy - height / 2);
  ctx.scale(s, s);
  if (icon.mode === 'fill') {
    ctx.fillStyle = YELLOW;
    icon.d.forEach((d) => ctx.fill(new Path2D(d), icon.rule ?? 'nonzero'));
  } else {
    ctx.strokeStyle = YELLOW;
    ctx.lineWidth = icon.sw ?? 2;
    ctx.lineCap = icon.cap ?? 'round';
    ctx.lineJoin = 'round';
    icon.d.forEach((d) => ctx.stroke(new Path2D(d)));
  }
  ctx.restore();
};

const drawGauge = (ctx: CanvasRenderingContext2D, fill: number): void => {
  ctx.lineWidth = 11;
  ctx.lineCap = 'round';
  // track
  ctx.strokeStyle = PAPER;
  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.arc(CX, GAUGE.yb, GAUGE.r, Math.PI, 2 * Math.PI);
  ctx.stroke();
  ctx.globalAlpha = 1;
  // fill (grows from the left over the top)
  if (fill > 0) {
    ctx.strokeStyle = YELLOW;
    ctx.beginPath();
    ctx.arc(CX, GAUGE.yb, GAUGE.r, Math.PI, Math.PI + fill * Math.PI);
    ctx.stroke();
  }
};

const drawProgress = (ctx: CanvasRenderingContext2D, index: number, labels: string[]): void => {
  const y = 431.5;
  const gap = 6;
  const segW = (SIZE - 2 * MARGIN - gap * 4) / 5;
  labels.forEach((label, i) => {
    const x = MARGIN + i * (segW + gap);
    const filled = i < index;
    ctx.fillStyle = filled ? YELLOW : 'rgba(255,255,255,0.16)';
    ctx.beginPath();
    ctx.roundRect(x, y, segW, 5, 2.5);
    ctx.fill();

    ctx.fillStyle = filled ? YELLOW : MUTED;
    ctx.font = `600 10px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.fillText(label.toUpperCase(), x + segW / 2, y + 22);
  });
};

const drawCard = (ctx: CanvasRenderingContext2D, { summary }: ShareInput): void => {
  const tier = milestoneTier(summary.energyKwh);

  // backdrop + rounded navy card (soft corners echo the template card)
  ctx.fillStyle = '#05080f';
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.fillStyle = NAVY;
  ctx.beginPath();
  ctx.roundRect(0, 0, SIZE, SIZE, 32);
  ctx.fill();

  // header: "SOLAR SURGE" wordmark (logo is drawn separately once rasterized)
  ctx.fillStyle = YELLOW;
  ctx.textAlign = 'right';
  ctx.letterSpacing = '2px';
  ctx.font = `700 15px ${FONT}`;
  ctx.fillText('SOLAR SURGE', SIZE - MARGIN, 62);
  ctx.letterSpacing = '0px';

  drawGauge(ctx, TIER_FILL[tier.index - 1]);

  // kWh + label inside the gauge
  ctx.textAlign = 'center';
  ctx.fillStyle = PAPER;
  ctx.font = `700 76px ${FONT}`;
  ctx.fillText(summary.energyKwh.toFixed(1), CX, 246);
  ctx.fillStyle = MUTED;
  ctx.letterSpacing = '1.5px';
  ctx.font = `600 13px ${FONT}`;
  ctx.fillText('KWH GENERATED', CX, 292);
  ctx.letterSpacing = '0px';

  // tier badge + name (icon and name centered together)
  const name = tier.label.toUpperCase();
  const nameBaseline = 345;
  ctx.font = `700 30px ${FONT}`;
  const nameWidth = ctx.measureText(name).width;
  const iconH = 30;
  const iconW = iconH * (TIER_ICONS[tier.index - 1].vb[0] / TIER_ICONS[tier.index - 1].vb[1]);
  const groupW = iconW + 12 + nameWidth;
  const groupX = CX - groupW / 2;
  drawIcon(ctx, TIER_ICONS[tier.index - 1], groupX + iconW / 2, nameBaseline - 10, iconH);
  ctx.textAlign = 'left';
  ctx.fillStyle = YELLOW;
  ctx.fillText(name, groupX + iconW + 12, nameBaseline);

  // level line
  ctx.textAlign = 'center';
  ctx.fillStyle = MUTED;
  ctx.font = `400 14px ${FONT}`;
  ctx.fillText(`Level ${summary.topLevel} · ${titleCase(summary.topLevelTitle)}`, CX, 382);

  drawProgress(
    ctx,
    tier.index,
    TIERS.map((t) => t.label)
  );

  // footer challenge
  ctx.fillStyle = PAPER;
  ctx.font = `600 15px ${FONT}`;
  ctx.fillText('Think you can beat my score?', CX, 512);
  ctx.fillStyle = YELLOW;
  ctx.font = `600 14px ${FONT}`;
  ctx.fillText('aurorasolar.com/home-solar/solar-surge', CX, 538);
};

const drawLogo = (ctx: CanvasRenderingContext2D, logo: HTMLImageElement): void => {
  const logoHeight = 26;
  const ratio = logo.width > 0 && logo.height > 0 ? logo.width / logo.height : 4;
  ctx.drawImage(logo, MARGIN, 44, logoHeight * ratio, logoHeight);
};

const toBlob = (canvas: HTMLCanvasElement): Promise<Blob | null> =>
  new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });

/** Renders the score card to a PNG file; resolves null when canvas 2d is unavailable (e.g. jsdom). */
export const renderScoreCardFile = async (input: ShareInput): Promise<File | null> => {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE * SCALE;
  canvas.height = SIZE * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.scale(SCALE, SCALE);

  drawCard(ctx, input);
  const logo = await loadLogo(input.logoSvg);
  if (logo) drawLogo(ctx, logo);
  const blob = await toBlob(canvas);
  if (!blob) return null;

  return new File([blob], 'solar-surge-score.png', { type: 'image/png' });
};

/**
 * Shares a pre-rendered card (Web Share API), falling back to a PNG download.
 * iOS Safari only allows navigator.share inside the user gesture's synchronous run, so render the
 * file ahead of the click and call this with nothing awaited in between.
 */
export const deliverScoreCardFile = async (file: File, energyKwh: number): Promise<'downloaded' | 'shared'> => {
  const text = `I banked ${energyKwh.toFixed(1)} kWh on Solar Surge. Think you can beat my high score?`;
  if (typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] })) {
    await navigator.share({ files: [file], text, title: 'Solar Surge' });
    return 'shared';
  }

  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.download = file.name;
  anchor.href = url;
  // Safari and Firefox only honor programmatic downloads from anchors attached to the document
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Firefox and older Safari start the download asynchronously after click() returns; revoking in
  // the same tick can cancel it, so defer the revoke.
  window.setTimeout(() => URL.revokeObjectURL(url), 10000);
  return 'downloaded';
};

/** Render-then-deliver fallback for when no pre-rendered file is available at click time. */
export const shareScoreCard = async (input: ShareInput): Promise<'downloaded' | 'shared' | null> => {
  const file = await renderScoreCardFile(input);
  return file ? deliverScoreCardFile(file, input.summary.energyKwh) : null;
};
